// ActivityLogs.jsx
import { useEffect, useMemo, useState } from 'react';
import api from '../api/axios';
import './ActivityLogs.css';

const TYPES = ['note', 'call', 'meeting', 'email', 'site_visit', 'follow_up', 'other'];

const TYPE_META = {
  note: { label: 'Note', badge: 'inactive', icon: 'N' },
  call: { label: 'Call', badge: 'active', icon: 'C' },
  meeting: { label: 'Meeting', badge: 'completed', icon: 'M' },
  email: { label: 'Email', badge: 'pending', icon: 'E' },
  site_visit: { label: 'Site Visit', badge: 'badge-design', icon: 'SV' },
  follow_up: { label: 'Follow-up', badge: 'badge-high', icon: 'F' },
  other: { label: 'Other', badge: 'inactive', icon: 'O' },
};

function cleanText(value) {
  return String(value || '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatDate(value, withTime = false) {
  if (!value) return 'Not set';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Not set';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: withTime ? undefined : 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
}

function followStatus(log) {
  if (!log.followUpDate) return { label: 'No follow-up', tone: 'muted' };
  if (log.followUpCompleted) return { label: 'Completed', tone: 'done' };
  const due = new Date(log.followUpDate);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  if (due < startToday) return { label: 'Overdue', tone: 'danger' };
  if (due >= startToday && due < endToday) return { label: 'Today', tone: 'today' };
  return { label: 'Upcoming', tone: 'upcoming' };
}

function ActivityCard({ log, onComplete }) {
  const meta = TYPE_META[log.type] || TYPE_META.other;
  const title = cleanText(log.title) || cleanText(log.description) || 'Untitled activity';
  const description = cleanText(log.description);
  const status = followStatus(log);

  return (
    <article className="activity-card">
      <div className="activity-icon">{meta.icon}</div>
      <div className="activity-main">
        <div className="activity-card-head">
          <div>
            <strong>{title}</strong>
            {description && description !== title ? <p>{description}</p> : null}
          </div>
          <span className={`badge badge-${meta.badge}`}>{meta.label}</span>
        </div>
        <div className="activity-meta-row">
          <span>Project: {log.project?.title || '-'}</span>
          <span>Client: {log.client?.name || log.project?.clientName || 'Client not linked'}</span>
          <span>Logged: {formatDate(log.createdAt)}</span>
        </div>
      </div>
      <aside className="activity-follow">
        <span className={`activity-status ${status.tone}`}>{status.label}</span>
        <b>{log.followUpDate ? formatDate(log.followUpDate, true) : '-'}</b>
        {log.followUpDate && !log.followUpCompleted ? (
          <button type="button" onClick={() => onComplete(log._id)}>
            Done
          </button>
        ) : null}
      </aside>
    </article>
  );
}

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ type: 'note', title: '', description: '', followUpDate: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [followFilter, setFollowFilter] = useState('all');

  const load = () => {
    setLoading(true);
    api.get('/activity-logs', { params: { limit: 100 } })
      .then((r) => {
        setLogs(r.data.data.logs || []);
        setSummary(r.data.data.summary || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/activity-logs', form);
      setShowModal(false);
      load();
      setForm({ type: 'note', title: '', description: '', followUpDate: '' });
    } catch (err) {
      alert(err.response?.data?.message || 'Failed.');
    } finally {
      setSaving(false);
    }
  };

  const completeFollowUp = async (id) => {
    try {
      await api.patch(`/activity-logs/${id}/complete-followup`);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not complete follow-up.');
    }
  };

  const h = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const stats = useMemo(() => {
    if (summary) {
      return {
        total: summary.totalLogs || 0,
        todayLogs: summary.todayLogs || 0,
        today: summary.dueToday || 0,
        overdue: summary.overdue || 0,
        pending: summary.pendingFollowUps || 0,
      };
    }
    const today = logs.filter((l) => followStatus(l).tone === 'today').length;
    const overdue = logs.filter((l) => followStatus(l).tone === 'danger').length;
    const pending = logs.filter((l) => l.followUpDate && !l.followUpCompleted).length;
    const now = new Date();
    const todayLogs = logs.filter((l) => {
      const d = new Date(l.createdAt);
      return !Number.isNaN(d.getTime())
        && d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate();
    }).length;
    return { total: logs.length, todayLogs, today, overdue, pending };
  }, [logs, summary]);

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((log) => {
      const status = followStatus(log).tone;
      const haystack = [
        log.title,
        log.description,
        log.type,
        log.project?.title,
        log.client?.name,
      ].map(cleanText).join(' ').toLowerCase();
      const matchesSearch = !q || haystack.includes(q);
      const matchesType = typeFilter === 'all' || log.type === typeFilter;
      const matchesFollow = followFilter === 'all'
        || (followFilter === 'pending' && log.followUpDate && !log.followUpCompleted)
        || (followFilter === status);
      return matchesSearch && matchesType && matchesFollow;
    });
  }, [logs, search, typeFilter, followFilter]);

  return (
    <div className="activity-page slide-up">
      <div className="page-header">
        <div className="page-header-left">
          <h1>Activity Logs</h1>
          <p>Track calls, notes, meetings, visits, and follow-ups in one place.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Log Activity</button>
      </div>

      <div className="activity-stats">
        <div><span>Total Logs</span><strong>{stats.total}</strong></div>
        <div><span>Today's Logs</span><strong>{stats.todayLogs}</strong></div>
        <div><span>Due Today</span><strong>{stats.today}</strong></div>
        <div><span>Overdue</span><strong>{stats.overdue}</strong></div>
        <div><span>Pending Follow-ups</span><strong>{stats.pending}</strong></div>
      </div>

      <div className="activity-toolbar">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, client, project..."
        />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All activity types</option>
          {TYPES.map((type) => <option key={type} value={type}>{TYPE_META[type].label}</option>)}
        </select>
        <select value={followFilter} onChange={(e) => setFollowFilter(e.target.value)}>
          <option value="all">All follow-ups</option>
          <option value="pending">Pending only</option>
          <option value="today">Due today</option>
          <option value="danger">Overdue</option>
          <option value="upcoming">Upcoming</option>
          <option value="done">Completed</option>
        </select>
      </div>

      <div className="activity-shell">
        {loading ? (
          <div className="activity-loading"><div className="spinner spinner-lg" /></div>
        ) : filteredLogs.length === 0 ? (
          <div className="empty-state">
            <h3>No activity found</h3>
            <p>Try another filter or log a new call, note, visit, or follow-up.</p>
          </div>
        ) : (
          <div className="activity-list">
            {filteredLogs.map((log) => (
              <ActivityCard key={log._id} log={log} onComplete={completeFollowUp} />
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Log Activity</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>x</button>
            </div>
            <div className="modal-body">
              <form id="log-form" onSubmit={save}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select className="form-select" name="type" value={form.type} onChange={h}>
                      {TYPES.map((type) => <option key={type} value={type}>{TYPE_META[type].label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Follow-up Date</label>
                    <input className="form-input" type="datetime-local" name="followUpDate" value={form.followUpDate} onChange={h} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input className="form-input" name="title" value={form.title} onChange={h} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" name="description" value={form.description} onChange={h} />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" type="submit" form="log-form" disabled={saving}>{saving ? 'Saving...' : 'Save Log'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
